import React, { useMemo, useState } from 'react';
import { simboloMoneda } from '../services/negocioActual';
import HistorialProductoModal from './HistorialProductoModal';
import { useCategorias } from '../context/CategoriasContext';
import { obtenerCategoria, obtenerFotoPrincipal, iconoCategoria } from '../utils/categorias';
import { optimizarFoto } from '../utils/imagen';
import { enOferta, precioEfectivo, pctOferta } from '../utils/precios';
import { categoriaUsa, valoresDeDimension, coincideDimension } from '../utils/agrupar';
import { aplicarOferta, quitarOferta, cambiarCatalogoVarios } from '../services/productosService';
import { aNumero } from '../utils/validacion';
import CampoNumero from './CampoNumero';
import { useToast } from '../context/ToastContext';

const fmt = (n) => `${simboloMoneda()} ${Number(n || 0).toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const th = { padding: '10px 12px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', verticalAlign: 'middle', whiteSpace: 'nowrap' };

// Calcula la ganancia de un producto: por unidad, % sobre el costo y total
// según el stock. Sirve para que Merly vea cuánto ganaría con lo que tiene.
function calcular(p) {
  const costo = Number(p.precio_costo || 0);
  const venta = precioEfectivo(p); // precio real (oferta si aplica)
  const stock = Number(p.stock || 0);
  const gananciaU = venta - costo;
  const pct = costo > 0 ? (gananciaU / costo) * 100 : 0;
  return {
    costo, venta, stock, gananciaU, pct,
    valorCosto: costo * stock,       // lo invertido en ese producto
    valorVenta: venta * stock,       // lo que vale a precio de venta
    gananciaTotal: gananciaU * stock, // ganancia potencial de todo su stock
  };
}

// Detalle de inventario: producto, costo, venta, ganancia y % por producto,
// con totales del inventario e historial de movimientos por producto.
export default function DetalleInventarioView({ productos = [], onVolver, onCambio }) {
  const { categorias } = useCategorias();
  const { mostrarToast } = useToast();
  const [busqueda, setBusqueda] = useState('');
  const [categoriaFiltro, setCategoriaFiltro] = useState(null); // null = todas
  const [filtroTipo, setFiltroTipo] = useState(null);
  const [filtroTalla, setFiltroTalla] = useState(null);
  const [filtroMarca, setFiltroMarca] = useState(null);
  const [historial, setHistorial] = useState(null); // producto cuyo historial se ve
  const [pctOf, setPctOf] = useState(''); // % de oferta a aplicar
  const [aplicando, setAplicando] = useState(false);
  const [seleccionados, setSeleccionados] = useState(() => new Set()); // ids marcados
  const [soloOcultos, setSoloOcultos] = useState(false); // 🙈 solo los que no se ven en el catálogo
  const [cambiosCatalogo, setCambiosCatalogo] = useState({}); // se ve al instante, sin recargar
  const [cambiandoCat, setCambiandoCat] = useState(false);

  // Categorías que realmente tienen productos, en el orden de la config.
  const categoriasConProductos = useMemo(() => {
    const presentes = new Set(productos.map((p) => p.categoria).filter(Boolean));
    const ordenadas = categorias.filter((c) => presentes.has(c.nombre)).map((c) => c.nombre);
    // Incluir categorías de productos que no estén en la config (por si acaso).
    presentes.forEach((n) => { if (!ordenadas.includes(n)) ordenadas.push(n); });
    return ordenadas;
  }, [productos, categorias]);

  const texto = busqueda.trim().toLowerCase();
  const lista = useMemo(() => productos
    .map((p) => (p.id in cambiosCatalogo ? { ...p, activo: cambiosCatalogo[p.id] } : p))
    .filter((p) => !soloOcultos || p.activo === false)
    .filter((p) => !categoriaFiltro || p.categoria === categoriaFiltro)
    .filter((p) => coincideDimension(p, 'subtipo', filtroTipo))
    .filter((p) => coincideDimension(p, 'talla', filtroTalla))
    .filter((p) => coincideDimension(p, 'marca', filtroMarca))
    .filter((p) =>
      !texto ||
      (p.nombre || '').toLowerCase().includes(texto) ||
      (p.marca || '').toLowerCase().includes(texto) ||
      (p.codigo || '').toLowerCase().includes(texto))
    .map((p) => ({ p, c: calcular(p) }))
    .sort((a, b) => b.c.gananciaTotal - a.c.gananciaTotal),
  [productos, cambiosCatalogo, soloOcultos, texto, categoriaFiltro, filtroTipo, filtroTalla, filtroMarca]);
  const totalOcultos = productos.filter((p) => (p.id in cambiosCatalogo ? cambiosCatalogo[p.id] : p.activo) === false).length;

  // Valores de los sub-filtros (solo cuando hay una categoría elegida).
  const prodsCat = categoriaFiltro ? productos.filter((p) => p.categoria === categoriaFiltro) : [];
  const tipos = categoriaFiltro && categoriaUsa(productos, categoriaFiltro, 'subtipo') ? valoresDeDimension(prodsCat, 'subtipo').valores : [];
  const tallas = categoriaFiltro && categoriaUsa(productos, categoriaFiltro, 'talla') ? valoresDeDimension(prodsCat, 'talla').valores : [];
  const marcas = categoriaFiltro && categoriaUsa(productos, categoriaFiltro, 'marca') ? valoresDeDimension(prodsCat, 'marca').valores : [];

  const elegirCategoria = (nombre) => {
    setCategoriaFiltro(nombre);
    setFiltroTipo(null);
    setFiltroTalla(null);
    setFiltroMarca(null);
    setSeleccionados(new Set());
  };

  // Totales del inventario (sobre lo filtrado).
  const tot = useMemo(() => lista.reduce((acc, { c }) => ({
    unidades: acc.unidades + c.stock,
    costo: acc.costo + c.valorCosto,
    venta: acc.venta + c.valorVenta,
    ganancia: acc.ganancia + c.gananciaTotal,
  }), { unidades: 0, costo: 0, venta: 0, ganancia: 0 }), [lista]);

  const pctTot = tot.costo > 0 ? (tot.ganancia / tot.costo) * 100 : 0;

  // Objetivo de la oferta: los marcados (si hay), si no, todos los mostrados.
  const idsMostrados = lista.map(({ p }) => p.id);
  const idsSel = idsMostrados.filter((id) => seleccionados.has(id));
  const idsObjetivo = idsSel.length > 0 ? idsSel : idsMostrados;
  const enOfertaCount = lista.filter(({ p }) => enOferta(p)).length;
  const subActivo = [filtroTipo, filtroTalla, filtroMarca].filter(Boolean).join(' · ');
  const nombreFiltro = (categoriaFiltro || (busqueda ? 'la búsqueda' : 'todos')) + (subActivo ? ` · ${subActivo}` : '');
  const todosSel = idsMostrados.length > 0 && idsMostrados.every((id) => seleccionados.has(id));

  const toggleSel = (id) => setSeleccionados((s) => {
    const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const toggleTodos = () => setSeleccionados((s) => {
    const n = new Set(s);
    if (todosSel) idsMostrados.forEach((id) => n.delete(id));
    else idsMostrados.forEach((id) => n.add(id));
    return n;
  });

  const aplicar = async () => {
    const pct = aNumero(pctOf);
    if (pct === null || pct <= 0 || pct >= 100) { mostrarToast('Escribe el descuento en números, entre 1 y 99', 'error'); return; }
    if (idsObjetivo.length === 0) return;
    setAplicando(true);
    try {
      const n = await aplicarOferta(idsObjetivo, pct);
      mostrarToast(`Oferta del ${pct}% aplicada a ${n} producto(s) 🏷️`, 'exito');
      setPctOf('');
      setSeleccionados(new Set());
      onCambio && onCambio();
    } catch (e) { mostrarToast('No se pudo aplicar la oferta', 'error'); }
    finally { setAplicando(false); }
  };

  // 🛍️ Ocultar/mostrar en el catálogo: los marcados (o todos los mostrados).
  const cambiarCatalogo = async (mostrar) => {
    if (idsObjetivo.length === 0) return;
    if (idsSel.length === 0 && !window.confirm(`No marcaste ninguno. ¿${mostrar ? 'Mostrar' : 'Ocultar'} los ${idsObjetivo.length} productos de ${nombreFiltro} ${mostrar ? 'en' : 'del'} catálogo?`)) return;
    setCambiandoCat(true);
    try {
      const n = await cambiarCatalogoVarios(idsObjetivo, mostrar);
      setCambiosCatalogo((c) => ({ ...c, ...Object.fromEntries(idsObjetivo.map((id) => [id, mostrar])) }));
      mostrarToast(mostrar ? `🛍️ Listo: ${n} producto(s) ya se ven en tu catálogo` : `🙈 Listo: ${n} producto(s) ya no se ven en tu catálogo`, 'exito', 4000);
      setSeleccionados(new Set());
      onCambio && onCambio();
    } catch (e) { mostrarToast('No se pudo cambiar el catálogo. Intenta de nuevo.', 'error'); }
    finally { setCambiandoCat(false); }
  };

  const quitar = async () => {
    if (idsObjetivo.length === 0) return;
    setAplicando(true);
    try {
      const n = await quitarOferta(idsObjetivo);
      mostrarToast(`Ofertas quitadas de ${n} producto(s) ✅`, 'exito');
      setSeleccionados(new Set());
      onCambio && onCambio();
    } catch (e) { mostrarToast('No se pudo quitar la oferta', 'error'); }
    finally { setAplicando(false); }
  };

  // Fila de píldoras para un sub-filtro (Tipo/Talla/Marca). No se muestra si no hay valores.
  const FilaSub = ({ etiqueta, valores, activo, onElegir }) => {
    if (!valores.length) return null;
    const pill = (sel) => ({ padding: '5px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
      border: `1px solid ${sel ? 'var(--color-primario,#DB2777)' : '#F9A8D4'}`,
      backgroundColor: sel ? 'var(--color-primario,#DB2777)' : 'white', color: sel ? 'white' : 'var(--color-primario,#DB2777)' });
    return (
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontSize: '12px', color: '#9CA3AF', marginRight: '2px', minWidth: '42px' }}>{etiqueta}:</span>
        <button onClick={() => onElegir(null)} style={pill(!activo)}>Todas</button>
        {valores.map((v) => (
          <button key={v} onClick={() => onElegir(v)} style={pill(activo === v)}>{v}</button>
        ))}
      </div>
    );
  };

  const Tarjeta = ({ titulo, valor, detalle, color }) => (
    <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '14px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid #F3F4F6' }}>
      <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>{titulo}</p>
      <p style={{ margin: 0, fontSize: '22px', fontWeight: 'bold', color }}>{valor}</p>
      {detalle && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#9CA3AF' }}>{detalle}</p>}
    </div>
  );

  return (
    <div className="k-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <h2 className="k-page-title">📊 Detalle de inventario</h2>
        <button onClick={onVolver} className="k-btn-soft">← Volver</button>
      </div>

      {/* Resumen del inventario */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px', marginBottom: '18px' }}>
        <Tarjeta titulo="Invertido (a costo)" color="#374151" valor={fmt(tot.costo)} detalle={`${tot.unidades} unidades en stock`} />
        <Tarjeta titulo="Valor a precio de venta" color="var(--color-primario,#DB2777)" valor={fmt(tot.venta)} detalle="Si vendes todo el stock" />
        <Tarjeta titulo="Ganancia potencial" color="var(--k-verde)" valor={fmt(tot.ganancia)} detalle={`${Math.round(pctTot)}% sobre lo invertido`} />
      </div>

      {/* Filtro por categoría */}
      {categoriasConProductos.length > 0 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
          <button onClick={() => elegirCategoria(null)}
            style={{ padding: '7px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              border: `1px solid ${categoriaFiltro === null ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`,
              backgroundColor: categoriaFiltro === null ? 'var(--color-primario,#DB2777)' : 'white',
              color: categoriaFiltro === null ? 'white' : '#6B7280' }}>
            Todas
          </button>
          {categoriasConProductos.map((nombre) => {
            const cat = obtenerCategoria(categorias, nombre);
            const activa = categoriaFiltro === nombre;
            return (
              <button key={nombre} onClick={() => elegirCategoria(nombre)}
                style={{ padding: '7px 14px', borderRadius: '999px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                  border: `1px solid ${activa ? 'var(--color-primario,#DB2777)' : '#E5E7EB'}`,
                  backgroundColor: activa ? 'var(--color-primario,#DB2777)' : 'white',
                  color: activa ? 'white' : '#6B7280' }}>
                {cat?.icono ? `${cat.icono} ` : ''}{nombre}
              </button>
            );
          })}
        </div>
      )}

      {/* Sub-filtros (Tipo / Talla / Marca) dentro de la categoría elegida */}
      {(tipos.length > 0 || tallas.length > 0 || marcas.length > 0) && (
        <div style={{ marginBottom: '12px' }}>
          <FilaSub etiqueta="Tipo" valores={tipos} activo={filtroTipo} onElegir={setFiltroTipo} />
          <FilaSub etiqueta="Talla" valores={tallas} activo={filtroTalla} onElegir={setFiltroTalla} />
          <FilaSub etiqueta="Marca" valores={marcas} activo={filtroMarca} onElegir={setFiltroMarca} />
        </div>
      )}

      <input
        type="search"
        placeholder="🔍 Buscar por nombre, marca o código"
        value={busqueda}
        onChange={(e) => setBusqueda(e.target.value)}
        style={{ width: '100%', padding: '12px', border: '1px solid #D1D5DB', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '12px', backgroundColor: 'white' }}
      />

      {/* Ofertas: aplicar/quitar descuento a los productos mostrados (por categoría o búsqueda) */}
      {lista.length > 0 && (
        <div style={{ backgroundColor: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#C2410C' }}>🏷️ Ofertas</span>
            <span style={{ fontSize: '12px', color: '#9A3412' }}>
              {idsSel.length > 0
                ? <>Aplica a <strong>{idsSel.length} seleccionado(s)</strong></>
                : <>Aplica a {idsMostrados.length} de <strong>{nombreFiltro}</strong> (o marcá algunos)</>}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto' }}>
              <CampoNumero valor={pctOf} onChange={setPctOf} deshabilitado={aplicando}
                decimales={0} min={1} max={99} placeholder="% ej. 20"
                maxMensaje="El descuento debe ser menor a 100%"
                estilo={{ width: '90px', padding: '8px', border: '1px solid #FDBA74', borderRadius: '8px', fontSize: '13px' }} />
              <button onClick={aplicar} disabled={aplicando}
                style={{ padding: '8px 14px', backgroundColor: '#EA580C', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                Aplicar
              </button>
              <button onClick={quitar} disabled={aplicando}
                style={{ padding: '8px 14px', backgroundColor: 'white', color: '#C2410C', border: '1px solid #FDBA74', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                Quitar oferta
              </button>
            </div>
          </div>
          {enOfertaCount > 0 && (
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#C2410C' }}>🔥 {enOfertaCount} en oferta ahora · se ven con precio tachado en el catálogo.</p>
          )}
        </div>
      )}

      {/* Catálogo: ocultar/mostrar en el catálogo en línea los marcados (o los mostrados) */}
      {(lista.length > 0 || soloOcultos) && (
        <div style={{ backgroundColor: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#5B21B6' }}>🛍️ Catálogo</span>
            <span style={{ fontSize: '12px', color: '#5B21B6' }}>
              {idsSel.length > 0
                ? <>Aplica a <strong>{idsSel.length} seleccionado(s)</strong></>
                : <>Marca los productos o aplica a los {idsMostrados.length} de <strong>{nombreFiltro}</strong></>}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto', flexWrap: 'wrap' }}>
              <button onClick={() => cambiarCatalogo(false)} disabled={cambiandoCat || !lista.length}
                style={{ padding: '8px 14px', backgroundColor: '#1F1A2E', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                🙈 No mostrar en el catálogo
              </button>
              <button onClick={() => cambiarCatalogo(true)} disabled={cambiandoCat || !lista.length}
                style={{ padding: '8px 14px', backgroundColor: 'white', color: '#047857', border: '1px solid #6EE7B7', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                👁️ Mostrar en el catálogo
              </button>
              {(totalOcultos > 0 || soloOcultos) && (
                <button onClick={() => { setSoloOcultos((v) => !v); setSeleccionados(new Set()); }}
                  style={{ padding: '8px 12px', backgroundColor: soloOcultos ? '#5B21B6' : 'white', color: soloOcultos ? 'white' : '#5B21B6', border: '1px solid #C4B5FD', borderRadius: '8px', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                  {soloOcultos ? '✕ Ver todos' : `Ver solo ocultos (${totalOcultos})`}
                </button>
              )}
            </div>
          </div>
          {cambiandoCat && <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#5B21B6' }}>⏳ Guardando…</p>}
        </div>
      )}

      {lista.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
          {soloOcultos ? 'No hay productos ocultos aquí. 🎉' : busqueda ? `Sin resultados para "${busqueda}".` : 'No hay productos todavía.'}
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #F3F4F6', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px', fontSize: '13px' }}>
            <thead>
              <tr style={{ backgroundColor: '#F9FAFB', color: '#6B7280', textAlign: 'left' }}>
                <th style={{ ...th, textAlign: 'center', width: '1%' }}>
                  <input type="checkbox" checked={todosSel} onChange={toggleTodos} title="Marcar/desmarcar todos" style={{ cursor: 'pointer' }} />
                </th>
                <th style={th}>Producto</th>
                <th style={{ ...th, textAlign: 'center' }}>Stock</th>
                <th style={{ ...th, textAlign: 'right' }}>Costo</th>
                <th style={{ ...th, textAlign: 'right' }}>Venta</th>
                <th style={{ ...th, textAlign: 'right' }}>Ganancia c/u</th>
                <th style={{ ...th, textAlign: 'right' }}>%</th>
                <th style={{ ...th, textAlign: 'right' }}>Ganancia total</th>
                <th style={{ ...th, textAlign: 'center' }}>Historial</th>
              </tr>
            </thead>
            <tbody>
              {lista.map(({ p, c }) => {
                const perdida = c.venta > 0 && c.gananciaU < 0;
                const colorGan = perdida ? '#DC2626' : 'var(--k-verde)';
                return (
                  <tr key={p.id} style={{ borderTop: '1px solid #F3F4F6', backgroundColor: seleccionados.has(p.id) ? '#FFF7ED' : 'transparent' }}>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <input type="checkbox" checked={seleccionados.has(p.id)} onChange={() => toggleSel(p.id)} style={{ cursor: 'pointer' }} />
                    </td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ flexShrink: 0, width: '40px', height: '40px', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#F9FAFB', border: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {obtenerFotoPrincipal(p)
                            ? <img src={optimizarFoto(obtenerFotoPrincipal(p), 150)} alt={p.nombre} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            : <span style={{ fontSize: '20px' }}>{iconoCategoria(categorias, p.categoria)}</span>}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: 600, color: '#111827' }}>{p.nombre}</span>
                          <span style={{ color: '#9CA3AF' }}> · {p.codigo}</span>
                          {p.activo === false && <span title="Tus clientas no lo ven en el catálogo en línea" style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 700, color: 'white', backgroundColor: '#1F1A2E', padding: '1px 7px', borderRadius: '999px', whiteSpace: 'nowrap' }}>🙈 Oculto</span>}
                        </div>
                      </div>
                    </td>
                    <td style={{ ...td, textAlign: 'center', fontWeight: 600, color: c.stock <= 0 ? '#DC2626' : '#374151' }}>{c.stock}</td>
                    <td style={{ ...td, textAlign: 'right', color: '#374151' }}>{fmt(c.costo)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: 'var(--color-primario,#DB2777)' }}>
                      {enOferta(p) ? (
                        <span>
                          <span style={{ color: '#9CA3AF', textDecoration: 'line-through', fontWeight: 400, fontSize: '11px', marginRight: '5px' }}>{fmt(p.precio_venta)}</span>
                          {fmt(c.venta)}
                          <span style={{ display: 'block', fontSize: '10px', fontWeight: 700, color: '#EA580C' }}>OFERTA −{pctOferta(p)}%</span>
                        </span>
                      ) : fmt(c.venta)}
                    </td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: colorGan }}>{fmt(c.gananciaU)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600, color: colorGan }}>{c.costo > 0 ? `${Math.round(c.pct)}%` : '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 'bold', color: colorGan }}>{fmt(c.gananciaTotal)}</td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      <button onClick={() => setHistorial(p)} title="Ver movimientos de este producto"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: 'var(--color-primario,#DB2777)', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                        📜 Ver historial
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ margin: '14px 0 0', fontSize: '12px', color: '#9CA3AF' }}>
        La ganancia se calcula como Precio de venta − Precio de costo. El % es sobre el costo. Toca 📜 para ver todos los movimientos (creación, ventas, ajustes, devoluciones) de cada producto.
      </p>

      {historial && (
        <HistorialProductoModal producto={historial} onCerrar={() => setHistorial(null)} />
      )}
    </div>
  );
}
